// ============================================================
// Console Super Admin — Gestion des comptes Super Admin
// ============================================================
import React, { useEffect, useState, useCallback } from 'react';
import { ShieldCheck, UserPlus, X, Lock, CheckCircle2, Loader2 } from 'lucide-react';
import superAdminApi from '../../services/superAdminApi';
import { useSuperAdminAuth } from '../../context/SuperAdminAuthContext';
import toast from 'react-hot-toast';

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const champCls = 'w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:border-blue-400';

// ── Modale : créer un Super Admin ───────────────────────────────────────────
function ModalCreer({ onClose, onCreated }) {
  const [f, setF] = useState({ nom: '', prenom: '', email: '', mot_de_passe: '', confirme: '', mot_de_passe_actuel: '' });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (f.mot_de_passe !== f.confirme) return toast.error('Les mots de passe ne correspondent pas.');
    if (f.mot_de_passe.length < 10) return toast.error('Mot de passe ≥ 10 caractères.');
    setSaving(true);
    try {
      await superAdminApi.post('/superadmin/super-admins', {
        nom: f.nom, prenom: f.prenom, email: f.email,
        mot_de_passe: f.mot_de_passe, mot_de_passe_actuel: f.mot_de_passe_actuel,
      });
      toast.success('Super Admin créé');
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h2 className="font-semibold text-slate-900 flex items-center gap-2"><UserPlus size={18} className="text-blue-600" /> Nouveau Super Admin</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Prénom</label>
              <input value={f.prenom} onChange={set('prenom')} className={champCls} required />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Nom</label>
              <input value={f.nom} onChange={set('nom')} className={champCls} required />
            </div>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Email</label>
            <input type="email" value={f.email} onChange={set('email')} className={champCls} required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Mot de passe (≥ 10)</label>
              <input type="password" value={f.mot_de_passe} onChange={set('mot_de_passe')} className={champCls} required />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Confirmer</label>
              <input type="password" value={f.confirme} onChange={set('confirme')} className={champCls} required />
            </div>
          </div>
          <div className="pt-2 border-t border-slate-100">
            <label className="block text-xs text-slate-500 mb-1 flex items-center gap-1.5"><Lock size={13} /> Votre mot de passe actuel (confirmation)</label>
            <input type="password" value={f.mot_de_passe_actuel} onChange={set('mot_de_passe_actuel')} className={champCls} required />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm bg-slate-100 hover:bg-slate-200 text-slate-700">Annuler</button>
            <button type="submit" disabled={saving} className="px-4 py-2 rounded-lg text-sm bg-blue-600 hover:bg-blue-700 text-white font-medium inline-flex items-center gap-2 disabled:opacity-60">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Créer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Modale : confirmer activer/désactiver ───────────────────────────────────
function ModalStatut({ sa, actif, onClose, onDone }) {
  const [mdp, setMdp] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await superAdminApi.patch(`/superadmin/super-admins/${sa.id}/actif`, { actif, mot_de_passe_actuel: mdp });
      toast.success(actif ? 'Compte réactivé' : 'Compte désactivé');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm">
        <div className="px-5 py-4 border-b border-slate-200">
          <h2 className="font-semibold text-slate-900">{actif ? 'Réactiver' : 'Désactiver'} ce Super Admin</h2>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <p className="text-sm text-slate-600">
            {actif ? 'Réactiver' : 'Désactiver'} <b>{sa.prenom} {sa.nom}</b> ({sa.email}).
            {!actif && ' Il ne pourra plus se connecter.'}
          </p>
          <div>
            <label className="block text-xs text-slate-500 mb-1 flex items-center gap-1.5"><Lock size={13} /> Votre mot de passe actuel</label>
            <input type="password" value={mdp} onChange={(e) => setMdp(e.target.value)} className={champCls} required autoFocus />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm bg-slate-100 hover:bg-slate-200 text-slate-700">Annuler</button>
            <button type="submit" disabled={saving}
              className={`px-4 py-2 rounded-lg text-sm text-white font-medium inline-flex items-center gap-2 disabled:opacity-60 ${actif ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}`}>
              {saving ? <Loader2 size={15} className="animate-spin" /> : null} Confirmer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function SuperAdmins() {
  const { superAdmin } = useSuperAdminAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [statutCible, setStatutCible] = useState(null); // { sa, actif }

  const charger = useCallback(async () => {
    try {
      const { data } = await superAdminApi.get('/superadmin/super-admins');
      setList(data || []);
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <ShieldCheck size={20} className="text-blue-600" /> Super Admins
          </h1>
          <p className="text-sm text-slate-400 mt-0.5">Comptes ayant accès complet à la plateforme. Chaque action est journalisée.</p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2 rounded-lg font-medium">
          <UserPlus size={16} /> Nouveau Super Admin
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-slate-400 border-b border-slate-200 bg-slate-50">
                <th className="text-left px-3 py-2">Nom</th>
                <th className="text-left px-3 py-2">Email</th>
                <th className="text-left px-3 py-2">Statut</th>
                <th className="text-left px-3 py-2">2FA</th>
                <th className="text-left px-3 py-2">Dernière connexion</th>
                <th className="text-right px-3 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">Chargement…</td></tr>
              ) : list.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">Aucun Super Admin.</td></tr>
              ) : (
                list.map((sa) => {
                  const moi = sa.id === superAdmin?.id;
                  return (
                    <tr key={sa.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-3 py-2 text-slate-900">
                        {sa.prenom} {sa.nom}{moi && <span className="ml-2 text-xs text-blue-600">(vous)</span>}
                      </td>
                      <td className="px-3 py-2 text-slate-500">{sa.email}</td>
                      <td className="px-3 py-2">
                        {sa.actif
                          ? <span className="inline-flex items-center gap-1.5 text-emerald-700"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Actif</span>
                          : <span className="inline-flex items-center gap-1.5 text-slate-400"><span className="w-2 h-2 rounded-full bg-slate-300" /> Désactivé</span>}
                      </td>
                      <td className="px-3 py-2 text-slate-500">{sa.deux_facteurs_actif ? '🔒 Activée' : '—'}</td>
                      <td className="px-3 py-2 text-slate-500">{formatDate(sa.derniere_connexion)}</td>
                      <td className="px-3 py-2 text-right">
                        {moi ? (
                          <span className="text-xs text-slate-300">—</span>
                        ) : sa.actif ? (
                          <button onClick={() => setStatutCible({ sa, actif: false })} className="text-xs text-red-600 hover:underline">Désactiver</button>
                        ) : (
                          <button onClick={() => setStatutCible({ sa, actif: true })} className="text-xs text-emerald-700 hover:underline">Réactiver</button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-slate-400">
        Astuce : un nouveau Super Admin pourra activer sa propre double authentification (2FA) depuis son profil après sa première connexion.
      </p>

      {showCreate && <ModalCreer onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); charger(); }} />}
      {statutCible && (
        <ModalStatut sa={statutCible.sa} actif={statutCible.actif}
          onClose={() => setStatutCible(null)}
          onDone={() => { setStatutCible(null); charger(); }} />
      )}
    </div>
  );
}
